import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';

interface AnalyticsData {
  userRegistrations: { date: string; count: number }[];
  testPerformance: { testId: string; testName: string; avgScore: number; completionRate: number }[];
  questionAnalytics: { questionId: string; correctRate: number; avgTime: number }[];
  userActivity: { date: string; activeUsers: number }[];
}

export const Analytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    userRegistrations: [],
    testPerformance: [],
    questionAnalytics: [],
    userActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [usersSnapshot, testsSnapshot, completedTestsSnapshot] = await Promise.all([
        get(ref(database, 'users')),
        get(ref(database, 'tests')),
        get(ref(database, 'completedTests'))
      ]);

      const users = usersSnapshot.exists() ? usersSnapshot.val() : {};
      const tests = testsSnapshot.exists() ? testsSnapshot.val() : {};
      const completedTests = completedTestsSnapshot.exists() ? completedTestsSnapshot.val() : {};

      // Process user registrations
      const registrations = processUserRegistrations(users);
      
      // Process test performance
      const testPerf = processTestPerformance(tests, completedTests);
      
      // Process user activity
      const activity = processUserActivity(completedTests);

      setAnalytics({
        userRegistrations: registrations,
        testPerformance: testPerf,
        questionAnalytics: [],
        userActivity: activity
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const processUserRegistrations = (users: any) => {
    const registrationsByDate: { [key: string]: number } = {};
    
    Object.values(users).forEach((user: any) => {
      if (user.createdAt) {
        const date = new Date(user.createdAt).toISOString().split('T')[0];
        registrationsByDate[date] = (registrationsByDate[date] || 0) + 1;
      }
    });

    return Object.entries(registrationsByDate)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30); // Last 30 days
  };

  const processTestPerformance = (tests: any, completedTests: any) => {
    const testStats: { [key: string]: { scores: number[]; totalMarks: number; name: string } } = {};

    Object.values(completedTests).forEach((userTests: any) => {
      Object.values(userTests || {}).forEach((test: any) => {
        if (test.testId && test.score !== undefined) {
          if (!testStats[test.testId]) {
            testStats[test.testId] = { 
              scores: [], 
              totalMarks: test.totalMarks || 100,
              name: test.name || 'Unknown Test'
            };
          }
          testStats[test.testId].scores.push(test.score);
        }
      });
    });

    return Object.entries(testStats).map(([testId, stats]) => ({
      testId,
      testName: stats.name,
      avgScore: stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length,
      completionRate: (stats.scores.length / Object.keys(completedTests).length) * 100
    }));
  };

  const processUserActivity = (completedTests: any) => {
    const activityByDate: { [key: string]: Set<string> } = {};
    
    Object.entries(completedTests).forEach(([userId, userTests]: [string, any]) => {
      Object.values(userTests || {}).forEach((test: any) => {
        if (test.attempted_at) {
          const date = new Date(test.attempted_at).toISOString().split('T')[0];
          if (!activityByDate[date]) {
            activityByDate[date] = new Set();
          }
          activityByDate[date].add(userId);
        }
      });
    });

    return Object.entries(activityByDate)
      .map(([date, userSet]) => ({ date, activeUsers: userSet.size }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30); // Last 30 days
  };

  const exportReport = (type: string) => {
    let data: any[] = [];
    let filename = '';

    switch (type) {
      case 'registrations':
        data = analytics.userRegistrations;
        filename = 'user_registrations.csv';
        break;
      case 'performance':
        data = analytics.testPerformance;
        filename = 'test_performance.csv';
        break;
      case 'activity':
        data = analytics.userActivity;
        filename = 'user_activity.csv';
        break;
    }

    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, subtitle, color }: any) => (
    <div className="bg-white p-6 rounded-lg shadow border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          <p className="text-3xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-24 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Analytics & Reporting</h2>
        <p className="text-gray-600">Comprehensive insights into user behavior and system performance</p>
      </div>

      <div className="mb-6">
        <nav className="flex space-x-4">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'users', label: 'User Analytics' },
            { id: 'tests', label: 'Test Analytics' },
            { id: 'reports', label: 'Reports' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Total Registrations (30 days)"
              value={analytics.userRegistrations.reduce((sum, day) => sum + day.count, 0)}
              color="#3B82F6"
            />
            <StatCard
              title="Average Test Score"
              value={analytics.testPerformance.length > 0 
                ? Math.round(analytics.testPerformance.reduce((sum, test) => sum + test.avgScore, 0) / analytics.testPerformance.length)
                : 0}
              subtitle="Across all tests"
              color="#10B981"
            />
            <StatCard
              title="Daily Active Users"
              value={analytics.userActivity.length > 0 
                ? Math.round(analytics.userActivity.reduce((sum, day) => sum + day.activeUsers, 0) / analytics.userActivity.length)
                : 0}
              subtitle="Average (30 days)"
              color="#F59E0B"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">User Registrations Trend</h3>
              <div className="space-y-2">
                {analytics.userRegistrations.slice(-7).map((day, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{day.date}</span>
                    <span className="font-semibold">{day.count} users</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Top Performing Tests</h3>
              <div className="space-y-2">
                {analytics.testPerformance
                  .sort((a, b) => b.avgScore - a.avgScore)
                  .slice(0, 5)
                  .map((test, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 truncate">{test.testName}</span>
                      <span className="font-semibold">{Math.round(test.avgScore)}%</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">User Registration Analytics</h3>
              <button
                onClick={() => exportReport('registrations')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registrations</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.userRegistrations.map((day, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{day.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{day.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tests' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Test Performance Analytics</h3>
              <button
                onClick={() => exportReport('performance')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.testPerformance.map((test, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{test.testName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(test.avgScore)}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(test.completionRate)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Generate Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => exportReport('registrations')}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
              >
                <h4 className="font-medium text-gray-900">User Registrations</h4>
                <p className="text-sm text-gray-600">Export user registration data</p>
              </button>
              <button
                onClick={() => exportReport('performance')}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
              >
                <h4 className="font-medium text-gray-900">Test Performance</h4>
                <p className="text-sm text-gray-600">Export test analytics data</p>
              </button>
              <button
                onClick={() => exportReport('activity')}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
              >
                <h4 className="font-medium text-gray-900">User Activity</h4>
                <p className="text-sm text-gray-600">Export user activity data</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};